import { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet'
import { Button, Input } from '@pilot-hub/ui'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

// Inner component for handling map clicks
const MapClickHandler = ({ onMapClick }: { onMapClick: (e: L.LeafletMouseEvent) => void }) => {
  useMapEvents({
    click: onMapClick,
  })
  return null
}

interface MapPickerProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  // Gespeicherte Koordinaten: Pin anzeigen, auch wenn im Feld bereits eine Adresse steht
  lat?: number | null
  lon?: number | null
}

export function MapPicker({ value, onChange, placeholder, disabled, lat, lon }: MapPickerProps) {
  const [showMap, setShowMap] = useState(false)
  const [mapCenter, setMapCenter] = useState<[number, number]>([50.1109, 8.6821])
  const [mapZoom, setMapZoom] = useState(10)
  const [markerPos, setMarkerPos] = useState<[number, number] | null>(null)
  const mapRef = useRef<L.Map>(null)

  useEffect(() => {
    if (value) {
      try {
        const coords = value.split(',').map(s => parseFloat(s.trim()))
        if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          setMarkerPos([coords[0], coords[1]])
          setMapCenter([coords[0], coords[1]])
          setMapZoom(15)
          return
        }
      } catch {}
    }
    if (value && lat != null && lon != null) {
      setMarkerPos([lat, lon])
      setMapCenter([lat, lon])
      setMapZoom(15)
    }
  }, [value, lat, lon])

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng
    setMarkerPos([lat, lng])
    setMapCenter([lat, lng])
    onChange(`${lat.toFixed(6)}, ${lng.toFixed(6)}`)
  }

  const handleGPS = () => {
    if (!navigator.geolocation) {
      alert('Geolocation wird von diesem Browser nicht unterstützt')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords
        setMarkerPos([latitude, longitude])
        setMapCenter([latitude, longitude])
        setMapZoom(15)
        onChange(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`)
      },
      (err) => {
        alert('Standort konnte nicht ermittelt werden: ' + err.message)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  const handleSearch = () => {
    const query = prompt('Ort suchen (z.B. "Frankfurt" oder "Hauptstraße 1, 60311 Frankfurt"):')
    if (!query) return

    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const lat = parseFloat(data[0].lat)
          const lon = parseFloat(data[0].lon)
          setMarkerPos([lat, lon])
          setMapCenter([lat, lon])
          setMapZoom(15)
          onChange(`${lat.toFixed(6)}, ${lon.toFixed(6)}`)
        } else {
          alert('Ort nicht gefunden')
        }
      })
      .catch(() => alert('Suche fehlgeschlagen'))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="w-full sm:w-1/2 sm:min-w-[280px]">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || 'z.B. 50.1109, 8.6821 oder Straße, PLZ Ort'}
            className="font-mono text-sm"
            disabled={disabled}
          />
        </div>
        <div className="w-full sm:w-1/2 flex flex-col gap-2">
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleGPS} disabled={showMap || disabled} className="flex-1">
              📍 GPS
            </Button>
            <Button variant="secondary" onClick={handleSearch} disabled={showMap || disabled} className="flex-1">
              🔍 Suchen
            </Button>
            <Button variant="secondary" onClick={() => setShowMap(!showMap)} disabled={disabled} className="flex-1">
              {showMap ? 'Karte schließen' : '🗺️ Karte'}
            </Button>
          </div>
        </div>
      </div>

      {markerPos && (
        <div className="text-xs text-dark-text-muted flex flex-wrap gap-4 items-center">
          <span>📍 Pin: {markerPos[0].toFixed(6)}, {markerPos[1].toFixed(6)}</span>
          {!disabled && (
            <Button variant="secondary" size="sm" onClick={() => {
              setMarkerPos(null)
              onChange('')
            }}>
              Pin entfernen
            </Button>
          )}
        </div>
      )}

      {showMap && (
        <div className="relative h-80 w-full rounded-lg border border-dark-border overflow-hidden">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom={true}
            className="h-full w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {markerPos && (
              <Marker position={markerPos}>
                <Popup>
                  Einsatzort: {markerPos[0].toFixed(6)}, {markerPos[1].toFixed(6)}
                </Popup>
              </Marker>
            )}
            <MapClickHandler onMapClick={handleMapClick} />
          </MapContainer>
          <Button
            variant="secondary"
            size="sm"
            className="absolute bottom-2 right-2 z-10"
            onClick={() => setShowMap(false)}
          >
            Karte schließen
          </Button>
        </div>
      )}
    </div>
  )
}